import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as d3 from "d3";
import {SidebarTab} from "../Sidebar";
import {
  IobrDataServerForRadarClusters,
  IobrDataServerForRadarDirections,
  ITypeRadar,
} from "../../types";
import {colors, EXScale, sizeCircleRadius} from "../../mocks/projectsMock";
import {ChangeDataForRadar} from "../../helper/ChangeDataForRadar";
import {
  IFintechFilters,
  signal_trend,
} from "../../pages/Fintech/FintechDashboard.type";
import {initialStateFintechFilters} from "../../pages/Fintech/FintechDashboard.const";
import ZoomableBlock from "./ZoomableBlock";
import {deepEqual} from "../../helper/functions";
import useCoordinates from "../../hooks/useCoordinates";
import {hideLabel, raiseAllTechnologyLabels} from "./RadarChart.functions";
import renderLogoImage from "./components/LogoImage";
import renderMaturityLabels from "./components/MaturityLabel";
import renderCircleRadii from "./components/CircleRadii";
import renderSegmentLabels from "./components/SegmentLabels";
import renderClusterPoint from "./components/ClusterPoint";
import calculateClusterCoordinates from "./helper/calculateClusterCoordinates";
import {IClustersMolecule} from "../../pages/Dashboard/Dashboard.type";

import renderClusterPointMolecule from "./components/ClusterPointMolecule";
import renderClusterPointMoleculeTwo from "./components/ClusterPointMoleculeTwo";
import useFetchDataLogotypeGpb from "../../hooks/useFetchDataLogotypeGpb";
// import useFetchDataNptlAll from "../../hooks/useFetchDataNptl";
import {Icoord} from "./components/SegmentLabel";

// interface IDirectionHere {
//   id: number | string;
//   name: string;
//   nptl: {
//     createdAt: string;
//     documentId: string;
//     id: number;
//     name: string;
//     publishedAt: string;
//     updatedAt: string;
//   };
// }
interface RadarChartProps {
  openSidebarWithTab: (
    tab: SidebarTab,
    directionId: number | string,
    clussterId?: number | string,
    name?: any,
    sectionName?: any,
    innerColor?: {back: string; color: string},
    nameTehnology?: string,
    idTehnology?: number | string,
    typeRadar?: ITypeRadar
  ) => void;
  isShowAllLabels: boolean;
  radarData: any;
  activeClusterId: number | string | null | undefined;
  activeDirectionId: number | string | null | undefined;
  typeRadar: ITypeRadar;
  filters?: IFintechFilters;
  isWeak_signal?: boolean;

  radarDataMolecula?: IClustersMolecule;
  onCloseMoleculaRadar?: () => void;
  idMoleculeActive?: number | string | null;
  activeTehnologyId?: number | string | null | undefined;
  idRndIndex?: number;

  isOpenPanel?: boolean;
  isOpenSideBarInfo?: boolean;

  isNationalProjects?: boolean;
  setIdWorkerShowConnections?: (id: string) => void;
  isEditMode?: boolean;
  manualPositions?: Array<{id: number | string; x: number; y: number}>;
  onPositionChange?: (id: number | string, x: number, y: number) => void;
}

const RadarChart: React.FC<RadarChartProps> = ({
  openSidebarWithTab,
  isShowAllLabels: showAllLabels,
  radarData,
  activeClusterId,
  activeDirectionId,
  typeRadar,
  filters,
  isWeak_signal,
  // signal_trend,
  radarDataMolecula,
  onCloseMoleculaRadar,
  idMoleculeActive,
  activeTehnologyId,
  idRndIndex,

  isOpenPanel,
  isOpenSideBarInfo,

  isNationalProjects,
  setIdWorkerShowConnections,
  isEditMode,
  manualPositions,
  onPositionChange,
}) => {
  const {settingData} = useFetchDataLogotypeGpb();

  const isShowLogotypeGpb = settingData?.data?.showGpb || false;
  // const isShowLogotypeGpb = false;
  const [scale, setScale] = useState(1);
  const [prevSignalTrend, setPrevSignalTrend] = useState<signal_trend>();
  const [prevWeakSignal, setWeakSignal] = useState<boolean>(false);
  const {coordinates, updateCoordinates, resetCoordinates} = useCoordinates();
  const [coordinatesDirectionActive, setCoordinatesDirectionActive] = useState<
    Icoord[]
  >([]);
  const [clusters, setClusters] = useState<IobrDataServerForRadarClusters[]>(
    []
  );

  const [directions, setDirections] = useState<
    IobrDataServerForRadarDirections[]
  >([]);
  const [dataMoleculaActive, setDataMoleculaActive] = useState<
    IClustersMolecule | undefined
  >(undefined);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const isFiltersNoActive: boolean = useMemo(
    () => deepEqual(filters || undefined, initialStateFintechFilters),
    [filters]
  );

  const [focusPosition, setFocusPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const segmentLabels = useMemo(
    () =>
      Array.isArray(directions)
        ? directions.map((d: any) => (d.name ? d.name : d.direction))
        : [],
    [directions]
  );

  const center = useMemo(
    () => ({x: dimensions.width / 2, y: dimensions.height / 2}),
    [dimensions]
  );

  const {
    fiveCircleRadius,
    fourthCircleRadius,
    thirdCircleRadius,
    secondCircleRadius,
    firstCircleRadius,
    logoRadius,
  } = sizeCircleRadius;

  const getMoleculaRadarData = useCallback(async () => {
    if (radarDataMolecula && radarDataMolecula.data) {
      setDataMoleculaActive(radarDataMolecula);
      const allDirectionsIds = radarDataMolecula.data
        .filter((item) => item.direction_id && item.direction_id !== null)
        .map((dir) => dir.direction_id);
      const uniqueDirectionsIds = allDirectionsIds.reduce(
        (unique: number[], id) => {
          if (!unique.includes(id)) {
            unique.push(id);
          }
          return unique;
        },
        []
      );

      const filteredDirections = directions.filter((direction) =>
        uniqueDirectionsIds.includes(Number(direction.id))
      );

      if (filteredDirections.length > 0) {
        setDirections(filteredDirections);
      }
    } else {
      setDataMoleculaActive(undefined);
    }
  }, [idMoleculeActive, directions, radarDataMolecula]);

  useEffect(() => {
    if (idMoleculeActive !== null && radarDataMolecula) {
      getMoleculaRadarData();
    }
    resetCoordinates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idMoleculeActive, typeRadar, radarDataMolecula, getMoleculaRadarData]);

  const getDataClusters = useCallback(() => {
    const {clustersNew, directions} =
      ChangeDataForRadar(
        radarData,
        typeRadar,
        isWeak_signal,
        radarDataMolecula?.data || dataMoleculaActive?.data
      ) || {};

    if (clustersNew) {
      setClusters(clustersNew);
    }

    if (directions) {
      if (typeRadar === "map") {
        const arrNptl = directions.map((item: any) => item.nptl);
        const arrUniqNptl = () => {
          const arrUniq: string[] = [];
          const returnUniqArr = [];
          if (arrNptl.length > 0) {
            for (let i = 0; i < arrNptl.length; i++) {
              if (
                arrNptl[i] &&
                arrNptl[i].id &&
                !arrUniq.includes(arrNptl[i].id)
              ) {
                arrUniq.push(arrNptl[i].id);
                returnUniqArr.push(arrNptl[i]);
              }
            }
          }
          return returnUniqArr;
        };
        const newArrDirections = arrUniqNptl()?.flatMap((nptl) =>
          directions.filter((direction: any) => direction?.nptl?.id === nptl.id)
        );
        setDirections(newArrDirections);
      } else {
        setDirections(directions);
      }
    }
  }, [
    radarData,
    typeRadar,
    isWeak_signal,
    dataMoleculaActive,
    radarDataMolecula,
  ]);

  useEffect(() => {
    getDataClusters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radarData, radarDataMolecula]);

  useEffect(() => {
    getDataClusters();
    resetCoordinates();
    if (isWeak_signal && isWeak_signal !== prevWeakSignal) {
      setWeakSignal(isWeak_signal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWeak_signal, radarDataMolecula]);

  useEffect(() => {
    if (!activeClusterId) return;
    const coordinatesActive = coordinates.find(
      (item) => item.id === activeClusterId
    );
    if (coordinatesActive) {
      const dx = +coordinatesActive.xEnd;
      const dy = +coordinatesActive.yEnd;
      setFocusPosition({x: dx, y: dy});
    }
  }, [activeClusterId]);

  useEffect(() => {
    if (activeClusterId) return;
    if (!activeDirectionId) return;
    const coordinatesActive = coordinatesDirectionActive.find(
      (item) => item.id === activeDirectionId
    );
    if (coordinatesActive) {
      const dx = +coordinatesActive.x;
      const dy = +coordinatesActive.y;
      setFocusPosition({x: dx, y: dy});
    }
  }, [activeDirectionId]);

  useEffect(() => {
    const handleResize = () => {
      setDimensions({width: window.innerWidth, height: window.innerHeight});
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const initializeSvg = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const mainGroup = svg
      .append("g")
      .attr("transform", `translate(${center.x},${center.y})`)
      .on("mouseup", function () {
        d3.select(this).interrupt();
      });

    renderCircleRadii(
      mainGroup,
      typeRadar,
      fiveCircleRadius,
      fourthCircleRadius,
      thirdCircleRadius,
      secondCircleRadius,
      firstCircleRadius,
      isWeak_signal,
      isNationalProjects
    );

    const quantitySections = segmentLabels.length || 0;
    if (typeRadar !== "moleculeTwo") {
      renderSegmentLabels({
        mainGroup,
        segmentLabels,
        quantitySections,
        colors,
        fourthCircleRadius,
        firstCircleRadius,
        isWeak_signal,
        activeDirectionId,
        directions,
        openSidebarWithTab,
        typeRadar,
        isNationalProjects,
        setCoordinatesDirectionActive,
        coordinatesDirectionActive,
      });
    }

    renderMaturityLabels(
      mainGroup,
      firstCircleRadius,
      secondCircleRadius,
      thirdCircleRadius,
      fourthCircleRadius,
      typeRadar,
      isWeak_signal
    );

    const sliceDirections =
      typeRadar === "moleculeTwo" ? directions.slice(0, 1) : directions;

    // Лучевой рендер
    if (typeof radarData != "undefined" && "directions" in radarData)
      sliceDirections.forEach((direction: any, segmentIndex: number) => {
        const backColor =
          direction.nptl?.id === 6
            ? "#69C303"
            : direction.nptl?.id === 2
            ? "#00F0FF"
            : direction.nptl?.id === 4
            ? "#FFA726"
            : direction.nptl?.id === 13
            ? "#BF3535"
            : direction.nptl?.id === 11
            ? "#FF00F2"
            : direction.nptl?.id === 8
            ? "#59FFAC"
            : "#11A0DA";

        let segmentAngle: number;
        if (typeRadar === "moleculeTwo") {
          segmentAngle = 360;
        } else {
          segmentAngle = quantitySections === 0 ? 0 : 360 / quantitySections;
        }

        const paddingAngle =
          quantitySections === 0
            ? 0
            : typeRadar === "molecula"
            ? 8
            : typeRadar === "moleculeTwo"
            ? 30
            : 6; // Отступы от границы сегмента

        const maturityLevels =
          typeRadar === "fintech"
            ? isWeak_signal
              ? ["Концепт", "Прототип"]
              : ["Концепт"]
            : ["Концепт", "Прототип", "Продукт", "Другое"];

        const maturityRadii: Record<string, [number, number]> = {
          Концепт:
            typeRadar === "fintech"
              ? isWeak_signal
                ? [
                    EXScale.coordWeakSignalLowStart,
                    EXScale.coordWeakSignalLowEnd,
                  ]
                : [EXScale.coordFintechStart, EXScale.coordFintechEnd]
              : [EXScale.coordConceptStart, EXScale.coordConceptEnd],
          Прототип: isWeak_signal
            ? [EXScale.coordWeakSignalHighStart, EXScale.coordWeakSignalHighEnd]
            : [EXScale.coordPrototypeStart, EXScale.coordPrototypeEnd],
          Продукт: [EXScale.coordProductStart, EXScale.coordProductEnd],
          Другое: [EXScale.coordOtherStart, EXScale.coordOtherEnd],
        };

        const segmentStartAngle = -90 + segmentIndex * segmentAngle;
        const segmentCenterAngle = segmentStartAngle + segmentAngle / 2;
        const availableAngle = segmentAngle - 2 * paddingAngle;

        const clustersByMaturity = maturityLevels.reduce((acc, maturity) => {
          acc[maturity] = (clusters as IobrDataServerForRadarClusters[]).filter(
            (cluster) =>
              cluster.direction_id === direction.id &&
              cluster.maturity === maturity
          );
          return acc;
        }, {} as Record<string, IobrDataServerForRadarClusters[]>);
        maturityLevels.forEach((maturity, clusterIndex) => {
          const clustersForMaturity = clustersByMaturity[maturity];
          const count = clustersForMaturity.length;

          if (count === 0) return;

          const [minRadius, maxRadius] = maturityRadii[maturity];

          let stepHeight: number;
          stepHeight = (maxRadius - minRadius) / (count - 1);
          clustersForMaturity.forEach(
            (cluster: IobrDataServerForRadarClusters, index) => {
              cluster.isLabelShown = false;
              const getCoordinates = calculateClusterCoordinates({
                cluster,
                count,
                index,
                maxRadius,
                minRadius,
                stepHeight,
                segmentCenterAngle,
                availableAngle,
                typeRadar,
                isWeak_signal,
                // signal_trend,
                prevSignalTrend,
                coordinates,
                updateCoordinates,
                isFiltersNoActive,
                maturity,
              });

              if (!getCoordinates) return;
              const {
                x,
                y,
                xStart,
                yStart,
                xEnd,
                yEnd,
                xMoleculaTwo,
                yMoleculaTwo,
                rayAngle,
              } = getCoordinates;

              const segmentColor = colors[segmentIndex % colors.length];
              const innerColor = segmentColor;
              const sectionName = directions[segmentIndex]?.name;

              if (typeof cluster !== "undefined") {
                const isActiveCluster = activeClusterId === cluster.id;
                if (typeRadar === "molecula") {
                  renderClusterPointMolecule({
                    cluster,
                    xStart,
                    yStart,
                    xMoleculaTwo,
                    yMoleculaTwo,
                    mainGroup,
                    segmentColor,
                    innerColor,
                    isActiveCluster,
                    showAllLabels,
                    openSidebarWithTab,
                    typeRadar,
                    sectionName,
                    setFocusPosition,
                    isWeak_signal,
                    scale,
                    hideLabel: (id?: number, idName?: string) =>
                      hideLabel(mainGroup, cluster.id, id, idName),
                    activeTehnologyId,
                  });
                } else {
                  if (typeRadar === "moleculeTwo") {
                    const activeTehnologyIdTwo = `${activeTehnologyId}-${idRndIndex}`;
                    renderClusterPointMoleculeTwo({
                      cluster,
                      xStart,
                      yStart,
                      xMoleculaTwo,
                      yMoleculaTwo,
                      mainGroup,
                      segmentColor,
                      innerColor,
                      isActiveCluster,
                      showAllLabels,
                      openSidebarWithTab,
                      typeRadar,
                      sectionName,
                      setFocusPosition,
                      isWeak_signal,
                      scale,
                      hideLabel: (id?: number, idName?: string) =>
                        hideLabel(mainGroup, cluster.id, id, idName),
                      activeTehnologyIdTwo,
                      index,
                      rayAngle,
                    });
                  } else {
                    const manualPos = manualPositions?.find(
                      (p) => p.id === cluster.id
                    );
                    renderClusterPoint({
                      cluster,
                      xStart: manualPos ? manualPos.x : xStart,
                      yStart: manualPos ? manualPos.y : yStart,
                      xEnd,
                      yEnd,
                      mainGroup,
                      segmentColor,
                      innerColor,
                      isActiveCluster,
                      showAllLabels,
                      openSidebarWithTab,
                      typeRadar,
                      sectionName,
                      setFocusPosition,
                      isWeak_signal,
                      scale,
                      hideLabel: (id?: number, idName?: string) =>
                        hideLabel(mainGroup, cluster.id, id, idName),
                      isNationalProjects,
                      backColor,
                      isEditMode,
                      onPositionChange,
                      segmentCenterAngle,
                      availableAngle: segmentAngle,
                      minRadius,
                      maxRadius,
                    });
                  }
                }
              }
            }
          );
        });
      });

    renderLogoImage(mainGroup, isShowLogotypeGpb, logoRadius.radius, typeRadar);
    raiseAllTechnologyLabels(mainGroup);
  }, [
    showAllLabels,
    radarData,
    typeRadar,
    clusters,
    directions,
    isWeak_signal,
    activeClusterId,
    activeDirectionId,
    activeTehnologyId,
    openSidebarWithTab,
    scale,
    isNationalProjects,
    center.x,
    center.y,
    coordinates,
    coordinatesDirectionActive,
    firstCircleRadius,
    fiveCircleRadius,
    fourthCircleRadius,
    idRndIndex,
    isFiltersNoActive,
    isShowLogotypeGpb,
    logoRadius.radius,
    prevSignalTrend,
    secondCircleRadius,
    segmentLabels,
    thirdCircleRadius,
    updateCoordinates,
    isEditMode,
    manualPositions,
    onPositionChange,
  ]);

  useEffect(() => {
    initializeSvg();
  }, [initializeSvg, radarDataMolecula, dimensions]);

  return (
    <div id="zoomableBlock">
      <ZoomableBlock
        focusPosition={focusPosition}
        scale={scale}
        setScale={setScale}
        isOpenPanel={isOpenPanel || false}
        isOpenSideBarInfo={isOpenSideBarInfo}
      >
        <svg ref={svgRef} width={dimensions.width} height={dimensions.height} />
      </ZoomableBlock>
    </div>
  );
};
export default RadarChart;
